import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  EventSeat,
  Check,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  Wifi,
  AcUnit,
  LocalCafe,
  WaterDrop,
  Power,
  LocalParking,
  Security,
  MenuBook,
  Business,
  Verified,
} from '@mui/icons-material';

const amenityIcons = {
  wifi: <Wifi />,
  ac: <AcUnit />,
  'air conditioning': <AcUnit />,
  cafeteria: <LocalCafe />,
  cafe: <LocalCafe />,
  water: <WaterDrop />,
  'drinking water': <WaterDrop />,
  power: <Power />,
  'power backup': <Power />,
  parking: <LocalParking />,
  security: <Security />,
  'reading room': <MenuBook />,
  locker: <Security />,
};

const getAmenityIcon = (amenity) => {
  const key = amenity.toLowerCase();
  return amenityIcons[key] || <Check />;
};

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function ProfessionalTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const defaultDescription = `${library.name} provides a professional environment designed for productive work and focused study. Our facility offers modern amenities and a conducive atmosphere for academic excellence.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8F9FA' }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business sx={{ color: accentColor }} />
            <Typography variant="h6" sx={{ color: '#1a1a1a', fontWeight: 600 }}>
              {library.name}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={onBookNow}
            sx={{
              bgcolor: accentColor,
              '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
            }}
          >
            Book Now
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ position: 'relative', height: { xs: '50vh', md: '65vh' } }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 100%)',
          }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)' }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)' }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        <Container maxWidth="lg" sx={{ position: 'absolute', bottom: 60, left: 0, right: 0 }}>
          <Box sx={{ maxWidth: 600 }}>
            <Chip
              icon={<Verified sx={{ color: 'white !important' }} />}
              label="Verified Library"
              size="small"
              sx={{ bgcolor: accentColor, color: 'white', mb: 2 }}
            />
            <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
              {library.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white', opacity: 0.9 }}>
              <LocationOn fontSize="small" />
              <Typography>{library.address}</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Stats Bar */}
      <Box sx={{ bgcolor: 'white', py: 3, borderBottom: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={4} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: accentColor }}>
                  {library.capacity}
                </Typography>
                <Typography variant="body2" color="text.secondary">Seats</Typography>
              </Box>
            </Grid>
            <Grid item xs={4} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: accentColor }}>
                  {library.amenities?.length || 0}+
                </Typography>
                <Typography variant="body2" color="text.secondary">Amenities</Typography>
              </Box>
            </Grid>
            <Grid item xs={4} md={2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: accentColor }}>
                  {plans.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Plans</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Left Column - About & Amenities */}
          <Grid item xs={12} md={8}>
            {/* About Section */}
            <Card elevation={0} sx={{ mb: 4, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ color: accentColor }} />
                  About the Library
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {library.description || defaultDescription}
                </Typography>
              </CardContent>
            </Card>

            {/* Amenities Section */}
            {library.amenities?.length > 0 && (
              <Card elevation={0} sx={{ mb: 4, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                    Facilities & Amenities
                  </Typography>
                  <Grid container spacing={2}>
                    {library.amenities.map((amenity, idx) => (
                      <Grid item xs={6} sm={4} key={idx}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 2,
                            bgcolor: '#f8f9fa',
                            borderRadius: 1,
                          }}
                        >
                          <Box sx={{ color: accentColor }}>
                            {React.cloneElement(getAmenityIcon(amenity), { fontSize: 'small' })}
                          </Box>
                          <Typography variant="body2">{amenity}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Gallery Section */}
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Gallery
                </Typography>
                <Grid container spacing={2}>
                  {images.slice(0, 6).map((img, idx) => (
                    <Grid item xs={6} md={4} key={idx}>
                      <Box
                        component="img"
                        src={img}
                        alt={`Gallery ${idx + 1}`}
                        onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                        loading="lazy"
                        decoding="async"
                        sx={{
                          width: '100%',
                          height: 150,
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': { transform: 'scale(1.02)' },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Plans & Contact */}
          <Grid item xs={12} md={4}>
            {/* Plans Card */}
            <Card elevation={0} sx={{ mb: 4, border: '1px solid #e0e0e0', position: 'sticky', top: 20 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Membership Plans
                </Typography>

                {plans.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {plans.map((plan) => (
                      <Box
                        key={plan.id}
                        sx={{
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'border-color 0.2s',
                          '&:hover': { borderColor: accentColor },
                        }}
                      >
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {plan.plan_type.replace('_', ' ')}
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: accentColor }}>
                            ₹{plan.price_cents}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={onBookNow}
                          sx={{ borderColor: accentColor, color: accentColor }}
                        >
                          Select
                        </Button>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography color="text.secondary" gutterBottom>
                      Contact us for pricing
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={onBookNow}
                  sx={{ bgcolor: accentColor, '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' } }}
                >
                  Book Now
                </Button>
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Contact Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <LocationOn sx={{ color: accentColor, mt: 0.5 }} />
                    <Typography variant="body2">{library.address}</Typography>
                  </Box>
                  {library.contact_phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Phone sx={{ color: accentColor }} />
                      <Typography variant="body2">
                        <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_phone}
                        </a>
                      </Typography>
                    </Box>
                  )}
                  {library.contact_email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Email sx={{ color: accentColor }} />
                      <Typography variant="body2">
                        <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_email}
                        </a>
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Map */}
                {library.lat && library.lng && (
                  <Box sx={{ mt: 3, height: 200, borderRadius: 1, overflow: 'hidden' }}>
                    <iframe
                      title="Location"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1a1a1a', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business sx={{ color: 'white' }} />
              <Typography sx={{ color: 'white' }}>{library.name}</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Powered by{' '}
              <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor }}>
                BookMyLibrary
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { bgcolor: 'black' } }}>
        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}>
          <Close />
        </IconButton>
        <Box component="img" src={images[currentImageIndex]} alt="Gallery" loading="lazy" decoding="async" sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Dialog>
    </Box>
  );
}

export default ProfessionalTheme;
